import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import {
  setRemember,
  useSignInMutation,
  useSocialAuthMutation,
} from '@/entities/session';
import { useGetUserInfoQuery } from '@/entities/user';
import { LoginForm } from '@/features/auth/login';
import { SocialButtons, useSocialLogin } from '@/features/auth/social-login';
import { parseApiError } from '@/shared/api/apiError';
import { useAppDispatch, useAppSelector } from '@/shared/model/storeHooks';
import { AuthFrame } from '@/widgets/auth-frame';

import s from '@/shared/ui/AuthForm.module.css';

export function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { rememberMe, isAdmin, accessToken, sessionEndedMessage } =
    useAppSelector((store) => store.auth);
  const [signIn, signInState] = useSignInMutation();
  const [socialAuth, socialAuthState] = useSocialAuthMutation();
  const currentError =
    parseApiError(signInState.error) || parseApiError(socialAuthState.error);
  const { data: userInfo } = useGetUserInfoQuery(undefined, {
    skip: !accessToken,
  });

  // "Remember Me" ticked: a longer session that survives closing the browser (AU-6-03, AU-6-04)
  function handleSubmit(data: { email: string; password: string }) {
    signIn({
      email: data.email,
      password: data.password,
      remember: !!rememberMe,
    });
  }

  const { loginWithGoogle, loginWithFacebook, handleAppleResponse } =
    useSocialLogin((socialType, accessToken, user) =>
      socialAuth({
        access_token: accessToken,
        socialType,
        user,
        remember: !!rememberMe,
      }),
    );

  useEffect(() => {
    if (!userInfo) return;
    if (userInfo.onboarding_finished && !isAdmin) {
      navigate('/dashboard');
    } else if (isAdmin) {
      navigate('/admin/customers');
    } else if (userInfo.email_added) {
      navigate('/success', {
        state: {
          isNewUser: true,
          userName: userInfo?.first_name,
          title: 'Welcome, _name_!',
          titleText:
            'Your account has been successfully created. Good luck with your journey!',
          btnText: 'Get Started',
          navPath: '/onboarding/plan',
        },
      });
    } else {
      navigate('/add-email');
    }
  }, [isAdmin, navigate, userInfo]);

  return (
    <AuthFrame
      spinning={signInState.isLoading || socialAuthState.isLoading}
      banner={sessionEndedMessage}
    >
      <div className={s.content}>
        <div className={s.split}>
          <div className={s.formSide}>
            <div className={s.card}>
              <h2 className={s.cardTitle}>{t('Sign In')}</h2>
              <LoginForm
                rememberMe={rememberMe}
                setRemember={(data) => dispatch(setRemember(data))}
                onSubmit={handleSubmit}
                error={currentError}
              >
                <div className={s.socialTitle}>
                  <span>{t('Sign In With')}</span>
                  <div className={s.socialLine} />
                </div>
                <SocialButtons
                  onGoogle={loginWithGoogle}
                  onFacebook={loginWithFacebook}
                  onApple={handleAppleResponse}
                  redirectPath="/login"
                />
                <div className={s.footerLink}>
                  {t('New to In_Home?')}{' '}
                  <Link to="/sign-up">{t('Get Started')}</Link>
                </div>
              </LoginForm>
            </div>
          </div>
          <div className={s.aside} />
        </div>
      </div>
    </AuthFrame>
  );
}
